import moment from 'moment-timezone';
import { useSelector } from 'react-redux';

import ProviderImage from '../../../../components/ProviderImage';

import * as api from '../../../../api';
import * as store from '../../../../reducers/store';

import { getProviderNameFriendly } from '../../../../utils/provider';
import { constructAddress } from '../../../../utils/practice';

import styles from './index.module.css';

import { ReactComponent as LeftArrow } from '../../../../assets/left_arrow.svg';
import { assertNever } from 'common/dist/utils/types';

interface Props {
  selectedEhrTimeSlot: store.SelectedEhrTimeSlot | null;
  onBackButtonClicked?: (errorMessage: string) => void;
  page?: number;
  providerConfig: api.User.EhrProviderInformationPractice | null;
  title?: string;
}

const Header: React.FC<Props> = ({
  selectedEhrTimeSlot,
  onBackButtonClicked,
  page,
  providerConfig,
  title,
}) => {
  const hasMultipleLocations = useSelector((store: store.All) => {
    const practiceId = selectedEhrTimeSlot?.practiceId;
    const providerUserId =
      selectedEhrTimeSlot?.providerId ?? providerConfig?.providerUserId;
    if (!providerUserId) return false;
    return (
      (store.inbound.ehrProviderInformation ?? []).filter(
        (providerInfo) =>
          (!practiceId || providerInfo.practiceId === practiceId) &&
          providerInfo.providerUserId === providerUserId,
      ).length > 1
    );
  });
  const fullIdentifier = (
    providerConfig: api.User.EhrProviderInformationPractice,
  ) => {
    const scheduleAppointmentDisplayBy =
      selectedEhrTimeSlot?.scheduleAppointmentDisplayBy ??
      api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy.Provider;
    switch (scheduleAppointmentDisplayBy) {
      case api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy
        .Provider:
        return getProviderNameFriendly(providerConfig, [
          'firstName',
          'lastName',
          'suffix',
        ]);
      case api.PrevisitFormTemplateFieldConfig.ScheduleAppointmentDisplayBy
        .Practice:
        return providerConfig.name;
      default:
        assertNever(
          'Unknown ScheduleAppointmentDisplayBy',
          scheduleAppointmentDisplayBy,
        );
    }
  };

  const address =
    !hasMultipleLocations && providerConfig && constructAddress(providerConfig);
  return (
    <div className={styles.ProviderHeader}>
      <div className={styles.HeaderContent}>
        <div className={styles.HeaderTitle}>
          {onBackButtonClicked && (page === undefined || page > 1) ? (
            <LeftArrow
              className={styles.BackArrow}
              onClick={() => onBackButtonClicked && onBackButtonClicked('')}
            />
          ) : null}
          {title}
        </div>
        {providerConfig ? (
          <div className={styles.ProviderInformation}>
            {providerConfig ? (
              <ProviderImage
                className={styles.ProviderImage}
                provider={
                  selectedEhrTimeSlot?.scheduleAppointmentDisplayBy ===
                  api.PrevisitFormTemplateFieldConfig
                    .ScheduleAppointmentDisplayBy.Practice
                    ? {
                        firstName: providerConfig.name,
                        lastName: '',
                        previsitImageUrl: '',
                      }
                    : providerConfig
                }
                size="Small"
              />
            ) : null}
            <div className={styles.ProviderDetails}>
              <div className={styles.ProviderTitle}>
                {fullIdentifier(providerConfig)}
              </div>
              <div className={styles.ProviderAddress}>
                {address ? address : null}
                {selectedEhrTimeSlot
                  ? `${address ? ' - ' : ''}${moment(
                      selectedEhrTimeSlot.startTime,
                    )
                      .tz(providerConfig.timezone)
                      .format('MMMM D, h:mm a')}`
                  : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Header;
